import React, { useRef, useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlay, faPause, faVolumeUp, faVolumeMute } from "@fortawesome/free-solid-svg-icons";
import "./VideoPlayer.scss";

interface Props {
  src: string;
  type: string;
  className?: string;
  autoPlay?: boolean;
  muted?: boolean;
  loop?: boolean;
}

export const VideoPlayer = ({ 
  src, 
  type, 
  className = "", 
  autoPlay = false, 
  muted = true, 
  loop = true 
}: Props) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(muted);
  const [showControls, setShowControls] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);

  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      const handleLoadedMetadata = () => {
        setDuration(video.duration);
      };

      const handleTimeUpdate = () => {
        setCurrentTime(video.currentTime);
      };

      const handlePlay = () => setIsPlaying(true);
      const handlePause = () => setIsPlaying(false);

      video.addEventListener('loadedmetadata', handleLoadedMetadata);
      video.addEventListener('timeupdate', handleTimeUpdate);
      video.addEventListener('play', handlePlay);
      video.addEventListener('pause', handlePause);

      return () => {
        video.removeEventListener('loadedmetadata', handleLoadedMetadata);
        video.removeEventListener('timeupdate', handleTimeUpdate);
        video.removeEventListener('play', handlePlay);
        video.removeEventListener('pause', handlePause);
      };
    }
  }, []);

  const togglePlay = () => {
    const video = videoRef.current;
    if (video) {
      if (isPlaying) {
        video.pause();
      } else {
        video.play();
      }
    }
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (video) {
      video.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div 
      className={`video-player-container ${className}`}
      onMouseEnter={() => setShowControls(true)}
      onMouseLeave={() => setShowControls(false)}
    >
      <video
        ref={videoRef}
        className="video-player"
        src={src}
        muted={isMuted}
        loop={loop}
        autoPlay={autoPlay}
        playsInline
        onClick={togglePlay}
      >
        <source src={src} type={type} />
        Your browser does not support HTML5 video.
      </video>
      
      {/* Overlay controls */}
      <div className={`video-overlay ${showControls ? 'show' : ''}`}>
        {/* Play/Pause button */}
        <button 
          className="play-button"
          onClick={togglePlay}
        >
          <FontAwesomeIcon 
            icon={isPlaying ? faPause : faPlay} 
            size="2x"
          />
        </button>

        {/* Bottom controls */}
        <div className="video-controls">
          <div className="time-display">
            {formatTime(currentTime)} / {formatTime(duration)}
          </div>
          
          <div className="control-buttons">
            <button 
              className="mute-button"
              onClick={toggleMute}
            >
              <FontAwesomeIcon 
                icon={isMuted ? faVolumeMute : faVolumeUp} 
                size="1x"
              />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
